import { writable, derived, get, type Writable, type Readable } from 'svelte/store';
import type {
  UserSettings,
  UserSettingsOptions,
  UserSettingsUpdate,
  UserSettingsOptionsUpdate
} from '../types/user-settings';

// Private writable store
const _userSettings: Writable<UserSettings | null> = writable(null);

// Public readable store (components should not directly set)
export const userSettings: Readable<UserSettings | null> = {
  subscribe: _userSettings.subscribe
};

// Derived stores for common access patterns
export const workOrderProfile: Readable<UserSettingsOptions['workOrderProfile'] | null> = derived(
  _userSettings,
  ($settings) => $settings?.options?.workOrderProfile ?? null
);

export const userLanguage: Readable<string | null> = derived(
  _userSettings,
  ($settings) => $settings?.options?.language ?? null
);

export const userPermissions: Readable<string[]> = derived(
  _userSettings,
  ($settings) => $settings?.permissions ?? []
);

// Check if user has a specific permission
export const hasPermission = (permission: string): Readable<boolean> =>
  derived(userPermissions, ($permissions) => $permissions.includes(permission));

/**
 * Store actions - these sync with legacy Backbone model
 */
export const userSettingsActions = {
  /**
   * Initialize store from legacy Backbone model
   * Called by box-bridge after auth-success
   */
  _setFromLegacy(settings: UserSettings): void {
    _userSettings.set(settings);
  },

  /**
   * Update specific fields
   * Syncs back to legacy Backbone model
   */
  update(updates: UserSettingsUpdate): void {
    _userSettings.update((current) => {
      if (!current) return current;
      const updated = { ...current, ...updates };
      // Sync to legacy
      _syncToLegacy(updated);
      return updated;
    });
  },

  /**
   * Update options specifically
   */
  updateOptions(optionUpdates: UserSettingsOptionsUpdate): void {
    _userSettings.update((current) => {
      if (!current) return current;
      const updated = {
        ...current,
        options: { ...current.options, ...optionUpdates }
      };
      // Sync to legacy
      _syncToLegacy(updated);
      return updated;
    });
  },

  /**
   * Get current value (non-reactive, use sparingly)
   */
  getCurrent(): UserSettings | null {
    return get(_userSettings);
  },

  /**
   * Clear store (on logout)
   */
  clear(): void {
    _userSettings.set(null);
  }
};

/**
 * Sync changes back to legacy Backbone model
 */
function _syncToLegacy(settings: UserSettings): void {
  try {
    const dataManager = window.Box?.Application?.getService('data-manager');
    if (dataManager) {
      const backboneModel = dataManager.getUserSettings();
      // Use Backbone's set method to trigger change events
      backboneModel.set(settings, { silent: false });
    }
  } catch (e) {
    console.warn('[TLShared] Failed to sync to legacy:', e);
  }
}

// Type declaration for window
declare global {
  interface Window {
    Box?: {
      Application: {
        getService: (name: string) => any;
        broadcast: (message: string, data?: any) => void;
      };
    };
  }
}
